import React, { ReactNode, useRef, useState } from 'react'
import styled from 'styled-components'
import { useDetectOutsideClick } from '../../hooks/useDetectOutsideClick'
import FilterIcon from '../../icons/IconLib/FilterIcon'

export interface FilterMenuButtonProps<T> {
    items: T[]
    value: T | null
    onChange: (item: T | null) => void
    getKey: (item: T) => string | number
    getLabel: (item: T) => string
    /** Optional swatch color per item. When provided, a colored dot is shown next to each row and on the active trigger. */
    getColor?: (item: T) => string | undefined
    /** Label for the "clear filter" row at the top of the menu. */
    allLabel?: string
    /** aria-label / title for the trigger button. */
    ariaLabel?: string
    /** Override the trigger icon. */
    icon?: ReactNode
    className?: string
}

export function FilterMenuButton<T>({
    items,
    value,
    onChange,
    getKey,
    getLabel,
    getColor,
    allLabel = 'הכל',
    ariaLabel,
    icon,
    className,
}: FilterMenuButtonProps<T>) {
    const wrapperRef = useRef<HTMLDivElement | null>(null)
    const [open, setOpen] = useState(false)

    useDetectOutsideClick(wrapperRef, () => setOpen(false))

    const isActive = value !== null
    const activeColor = isActive && getColor ? getColor(value as T) : undefined
    const triggerLabel = isActive ? getLabel(value as T) : ariaLabel || 'סינון'

    const handleSelect = (item: T | null) => {
        onChange(item)
        setOpen(false)
    }

    return (
        <Wrapper ref={wrapperRef} className={className}>
            <Trigger
                type='button'
                aria-haspopup='listbox'
                aria-expanded={open}
                aria-label={triggerLabel}
                $active={isActive}
                $open={open}
                onClick={() => setOpen((v) => !v)}
            >
                {icon ?? <FilterIcon />}
                {isActive && <Dot $color={activeColor} />}
            </Trigger>

            {open && (
                <Dropdown role='listbox'>
                    <Item
                        role='option'
                        aria-selected={value === null}
                        $active={value === null}
                        onMouseDown={(e) => {
                            e.preventDefault()
                            handleSelect(null)
                        }}
                    >
                        {getColor && <Swatch $color='transparent' />}
                        <Label>{allLabel}</Label>
                    </Item>
                    {items.map((item) => {
                        const key = getKey(item)
                        const selected = value !== null && getKey(value) === key
                        const color = getColor?.(item)
                        return (
                            <Item
                                key={String(key)}
                                role='option'
                                aria-selected={selected}
                                $active={selected}
                                onMouseDown={(e) => {
                                    e.preventDefault()
                                    handleSelect(item)
                                }}
                            >
                                {getColor && <Swatch $color={color ?? 'transparent'} />}
                                <Label>{getLabel(item)}</Label>
                            </Item>
                        )
                    })}
                </Dropdown>
            )}
        </Wrapper>
    )
}

export default FilterMenuButton

const Wrapper = styled.div`
    position: relative;
    flex-shrink: 0;
`

const Trigger = styled.button<{ $active: boolean; $open: boolean }>`
    position: relative;
    box-sizing: border-box;
    width: 2.875rem;
    height: 2.875rem;
    background: ${({ theme, $active }) =>
        $active ? theme.colors.accentSoft : theme.colors.surface};
    border: 1px solid
        ${({ theme, $active, $open }) =>
            $open || $active ? theme.colors.ink : theme.colors.inputBorder};
    border-radius: 0.875rem;
    color: ${({ theme, $active }) => ($active ? theme.colors.ink : theme.colors.icon)};
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition:
        border-color ${({ theme }) => theme.motion.fast} ease,
        background ${({ theme }) => theme.motion.fast} ease,
        box-shadow ${({ theme }) => theme.motion.fast} ease,
        color ${({ theme }) => theme.motion.fast} ease;

    box-shadow: ${({ theme, $open }) => ($open ? theme.shadows.focus : 'none')};

    &:hover {
        border-color: ${({ theme }) => theme.colors.inputBorderHover};
        color: ${({ theme }) => theme.colors.ink};
    }

    svg {
        width: 1.125rem;
        height: 1.125rem;
    }

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        width: 2.5rem;
        height: 2.5rem;

        svg {
            width: 1rem;
            height: 1rem;
        }
    }
`

const Dot = styled.span<{ $color?: string }>`
    position: absolute;
    top: 0.375rem;
    inset-inline-end: 0.375rem;
    width: 0.5rem;
    height: 0.5rem;
    border-radius: 999px;
    background: ${({ $color, theme }) => $color || theme.colors.accent};
    border: 1.5px solid ${({ theme }) => theme.colors.surface};
`

const Dropdown = styled.div`
    position: absolute;
    top: calc(100% + 6px);
    inset-inline-end: 0;
    z-index: 30;
    min-width: 12rem;
    background: ${({ theme }) => theme.colors.surface};
    border: 1px solid ${({ theme }) => theme.colors.inputBorder};
    border-radius: 0.875rem;
    box-shadow: ${({ theme }) => theme.shadows.card};
    padding: 4px;
    direction: rtl;
    max-height: 18rem;
    overflow-y: auto;
    scrollbar-width: none;
    -ms-overflow-style: none;

    &::-webkit-scrollbar {
        width: 0;
        height: 0;
    }
`

const Item = styled.div<{ $active: boolean }>`
    display: flex;
    align-items: center;
    gap: 0.625rem;
    padding: 0.5rem 0.75rem;
    border-radius: 0.5rem;
    cursor: pointer;
    background: ${({ $active, theme }) => ($active ? theme.colors.accentSoft : 'transparent')};
    transition: background ${({ theme }) => theme.motion.fast} ease;

    &:hover {
        background: ${({ theme, $active }) =>
            $active ? theme.colors.accentSoft : theme.colors.line2};
    }
`

const Swatch = styled.span<{ $color: string }>`
    width: 0.75rem;
    height: 0.75rem;
    border-radius: 999px;
    background: ${({ $color }) => $color};
    border: 1px solid
        ${({ $color, theme }) =>
            $color === 'transparent' ? theme.colors.inputBorder : 'transparent'};
    flex-shrink: 0;
`

const Label = styled.span`
    font-family: ${({ theme }) => theme.fonts.sans};
    font-size: ${({ theme }) => theme.text.md};
    color: ${({ theme }) => theme.colors.ink};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`
